/**
 * behavioralCommerce.js — Shadow tracking for Kaiso Revenue Intelligence.
 *
 * Fire-and-forget events to POST /api/v1/behavioral-commerce/events.
 * Uses navigator.sendBeacon when available; never blocks UI or checkout.
 * All dispatches are deferred (queueMicrotask) — zero render blocking.
 */
const SESSION_KEY = "kaiso_session_id";
const SESSION_TS_KEY = "kaiso_session_created_at";
const VISIT_KEY = "kaiso_bc_visit_count";
const INIT_KEY = "kaiso_bc_init_rid";

/** Session TTL — 30 days; persistent across refresh, renewed on expiry. */
const SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Per-product view cooldown — prevents rerender / scroll flood. */
const PRODUCT_VIEW_COOLDOWN_MS = 30_000;

let _config = null;
const _eventCooldown = new Map();

function getKaisoSessionId() {
  if (typeof window === "undefined") return "ssr";
  const now = Date.now();
  const created = parseInt(localStorage.getItem(SESSION_TS_KEY) || "0", 10);
  let sid = localStorage.getItem(SESSION_KEY);

  if (!sid || !created || now - created > SESSION_TTL_MS) {
    sid = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, sid);
    localStorage.setItem(SESSION_TS_KEY, String(now));
  }
  return sid;
}

function bumpVisitCount() {
  const count = parseInt(localStorage.getItem(VISIT_KEY) || "0", 10) + 1;
  localStorage.setItem(VISIT_KEY, String(count));
  return count;
}

function inferDeviceType() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/tablet|ipad/i.test(ua)) return "tablet";
  if (/mobile|android|iphone/i.test(ua)) return "mobile";
  return "desktop";
}

function captureAttribution() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer || "";
  const source = params.get("utm_source") || "";
  return {
    source: source || undefined,
    medium: params.get("utm_medium") || undefined,
    campaign: params.get("utm_campaign") || undefined,
    fbclid: params.get("fbclid") || undefined,
    referrer: referrer.slice(0, 500) || undefined,
  };
}

function inferEntryEvent(attr) {
  const src = (attr.source || "").toLowerCase();
  const ref = (attr.referrer || "").toLowerCase();
  if (src.includes("instagram") || ref.includes("instagram")) return "instagram_entry";
  if (src.includes("facebook") || src === "fb" || ref.includes("facebook")) return "facebook_entry";
  if (src.includes("google") || ref.includes("google")) return "google_entry";
  if (src || ref) return "direct_entry";
  return null;
}

function getCookie(name) {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? match[1] : undefined;
}

function _shouldSkipCooldown(key, ms) {
  const now = Date.now();
  const last = _eventCooldown.get(key) || 0;
  if (now - last < ms) return true;
  _eventCooldown.set(key, now);
  return false;
}

function _dispatchSend(url, body) {
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    }
  } catch { /* fallback */ }

  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
    credentials: "omit",
  }).catch(() => {});
}

/**
 * @param {{ restaurantId: string, apiBase?: string, currency?: string }} opts
 */
export function initBehavioralCommerce({ restaurantId, apiBase = "", currency = "EUR" }) {
  if (typeof window === "undefined") return;

  // Guard: one init per restaurant per page load (effect re-runs safe)
  const initMarker = `${INIT_KEY}:${restaurantId}`;
  if (sessionStorage.getItem(initMarker)) return;
  sessionStorage.setItem(initMarker, "1");

  _config = { restaurantId, apiBase, currency, sessionId: getKaisoSessionId() };
  const visits = bumpVisitCount();
  const attr = captureAttribution();

  bcTrack("menu_page_view", { path: window.location.pathname, metadata: { visit_count: visits } });
  const entry = inferEntryEvent(attr);
  if (entry) bcTrack(entry, { metadata: { entry: true } });
  if (visits > 1) bcTrack("return_visit", { metadata: { visit_count: visits } });
}

export function bcTrack(eventName, data = {}) {
  if (!_config?.restaurantId) return;

  const defer = typeof queueMicrotask === "function"
    ? queueMicrotask
    : (fn) => setTimeout(fn, 0);

  defer(() => {
    try {
      _bcTrackImmediate(eventName, data);
    } catch { /* shadow — never propagate */ }
  });
}

/** Product view with per-product debounce (30s) — safe for rerenders. */
export function bcTrackProductView(productId, data = {}) {
  if (!productId) return;
  const key = `pv:${productId}`;
  if (_shouldSkipCooldown(key, PRODUCT_VIEW_COOLDOWN_MS)) return;
  bcTrack("product_view", { product_id: productId, ...data });
}

function _bcTrackImmediate(eventName, data = {}) {
  const attr = captureAttribution();
  const payload = {
    restaurant_id: _config.restaurantId,
    event_name: eventName,
    session_id: _config.sessionId,
    currency: _config.currency,
    source: data.source ?? attr.source,
    medium: data.medium ?? attr.medium,
    campaign: data.campaign ?? attr.campaign,
    fbclid: data.fbclid ?? attr.fbclid,
    fbp: getCookie("_fbp"),
    fbc: getCookie("_fbc"),
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent?.slice(0, 300) : undefined,
    device_type: inferDeviceType(),
    path: data.path ?? (typeof window !== "undefined" ? window.location.pathname : undefined),
    product_id: data.product_id,
    category_id: data.category_id,
    cart_value: data.cart_value,
    order_id: data.order_id,
    amount: data.amount,
    metadata: data.metadata,
  };

  const body = JSON.stringify(payload);
  if (body.length > 8000) return;

  const base = (_config.apiBase || "").replace(/\/$/, "");
  const url = `${base}/api/v1/behavioral-commerce/events`;
  _dispatchSend(url, body);
}

export { getKaisoSessionId };
